import React, { useCallback, useEffect, useState } from 'react';
import api from '../../../services/api';
import { Button } from '../../ui/button';
import { toast } from 'sonner';
import { Loader2, Trophy } from 'lucide-react';
import { Badge } from './WbAdminTables';

const fmt = (n) => Number(n || 0).toLocaleString('pt-BR');

/* Rankings (doces + afiliados) e prêmios finais: salvos no encerramento, creditados só com aprovação. */
export default function WbAdminPrizes({ ended }) {
  const [rank, setRank] = useState(null);
  const [pay, setPay] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    try {
      const [r, p] = await Promise.all([api.get('/admin/wb/rankings'), api.get('/admin/wb/payouts')]);
      setRank(r.data); setPay(p.data);
    } catch { toast.error('Erro ao carregar rankings.'); }
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!rank) return <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-orange-500" /></div>;

  const aprovar = async (kind, rankN) => {
    const alvo = rankN ? `o ${rankN}º lugar` : 'TODOS os prêmios';
    if (!window.confirm(`Liberar ${alvo} de ${pay.kinds[kind]}? As moedas serão creditadas DE VERDADE na KAKO.`)) return;
    setBusy(true);
    try { await api.post('/admin/wb/payouts/approve', { kind, rank: rankN || null }); toast.success('Prêmio processado.'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Erro ao aprovar.'); }
    setBusy(false);
  };

  const Lista = ({ titulo, rows, val, testid }) => (
    <div className="rounded-xl border p-3" data-testid={testid}>
      <p className="mb-2 text-xs font-black uppercase tracking-wider text-orange-700">{titulo}</p>
      {rows.length === 0 && <p className="text-xs text-slate-400">Ninguém ainda.</p>}
      <ol className="space-y-1 text-xs">
        {rows.slice(0, 20).map((r) => (
          <li key={r.rank} className="flex items-center gap-2">
            <b className="w-6">{r.rank}º</b>
            {r.avatar ? <img src={r.avatar} alt="" className="h-6 w-6 rounded-full object-cover" /> : <span className="h-6 w-6 rounded-full bg-slate-200" />}
            <span className="min-w-0 flex-1 truncate">{r.nickname || r.name} <span className="text-slate-400">{r.kako_id ? `· ${r.kako_id}` : ''}</span></span>
            <b>{val(r)}</b>
            {r.prize > 0 && <span className="text-emerald-700">prêmio {fmt(r.prize)}</span>}
          </li>
        ))}
      </ol>
    </div>
  );

  return (
    <div className="space-y-3" data-testid="wb-admin-prizes">
      <Lista titulo="Ranking dos Bruxos — balões estourados (contas de teste fora)" rows={rank.rows} val={(r) => `${fmt(r.pops)} balões`} testid="wb-admin-rank-pops" />
      <div className="rounded-xl border p-3" data-testid="wb-admin-payouts">
        <p className="mb-1 flex items-center gap-2 text-xs font-black uppercase tracking-wider text-orange-700"><Trophy className="h-4 w-4" /> Prêmios finais — liberação manual</p>
        <p className="mb-3 rounded-lg border border-amber-300 bg-amber-50 p-2 text-xs font-bold text-amber-800">
          Nada é creditado automaticamente. No encerramento os ganhadores ficam salvos aqui e as moedas só saem da conta KAKO quando você aprovar.
          {!ended && ' O evento ainda está em andamento — os prêmios aparecem quando ele encerrar.'}
        </p>
        {Object.entries(pay?.kinds || {}).map(([kind, label]) => {
          const rows = (pay.rows || []).filter((r) => r.kind === kind);
          if (!rows.length) return null;
          const pend = rows.filter((r) => r.status !== 'PAID').length;
          return (
            <div key={kind} className="mb-3 space-y-1" data-testid={`wb-admin-payout-${kind}`}>
              <div className="flex items-center justify-between">
                <p className="text-xs font-bold uppercase text-slate-600">{label} · {fmt(rows.reduce((s, r) => s + r.coins, 0))} moedas · {pend} a liberar</p>
                {pend > 0 && <Button size="sm" disabled={busy} className="bg-emerald-600 text-white hover:bg-emerald-700" onClick={() => aprovar(kind)} data-testid={`wb-admin-approve-all-${kind}`}>Aprovar e creditar todos</Button>}
              </div>
              {rows.map((r) => (
                <div key={r.rank} className="flex flex-wrap items-center gap-2 rounded-lg border p-2 text-xs" data-testid={`wb-admin-payout-${kind}-${r.rank}`}>
                  <b className="w-8">{r.rank}º</b>
                  <span className="text-slate-600">{r.nickname} · KAKO {r.kako_id}</span>
                  <b className="text-emerald-700">{fmt(r.coins)} moedas</b>
                  <Badge s={r.status === 'AWAITING_APPROVAL' ? 'PENDING' : r.status} />
                  {r.status === 'AWAITING_APPROVAL' && <span className="text-[10px] text-slate-500">aguardando sua aprovação</span>}
                  {r.message && <span className="text-slate-400">{r.message}</span>}
                  {r.status !== 'PAID' && (
                    <Button size="sm" variant="outline" disabled={busy} onClick={() => aprovar(kind, r.rank)} data-testid={`wb-admin-approve-${kind}-${r.rank}`}>
                      {r.status === 'FAILED' ? 'Tentar de novo' : 'Aprovar e creditar'}
                    </Button>
                  )}
                </div>
              ))}
            </div>
          );
        })}
        {pay && (pay.rows || []).length === 0 && <p className="text-xs text-slate-400">Nenhum prêmio salvo ainda.</p>}
      </div>
    </div>
  );
}
